import { useState } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import { useAppDispatch } from "../../store/store";
import { addB2BEntity } from "../../store/thunk/billingThunk";

// Mapping frontend auth type names to authTypeId from DB
const authTypeMap: Record<string, number> = {
  "None": 1,
  "HMAC": 2,
  "Bearer Token": 3,
  "Basic Auth": 4,
  "Custom Header": 5,
};

export function AddB2BEntityDialog({ open, handleClose }: any) {
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState({
    name: "",
    type: "",
    questionnairePrefillUrl: "",
    questionnaireSaveUrl: "",
    questionnaireCompleteUrl: "",
    authType: "None", // Default auth type
    hmacSignatureValue: "",
    bearerTokenValue: "",
    basicAuthClientId: "",
    basicAuthSecret: "",
    customHeaderName: "",
    customHeaderValue: ""
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [btnLoading, setBtnLoading] = useState(false);

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdate = () => {
    const errors: Record<string, string> = {};

    if (!formData.name) errors.name = "Name is required.";
    if (!formData.type) errors.type = "Type is required.";

    switch (formData.authType) {
      case "HMAC":
        if (!formData.hmacSignatureValue) errors.hmacSignatureValue = "HMAC Signature Value is required.";
        break;
      case "Bearer Token":
        if (!formData.bearerTokenValue) errors.bearerTokenValue = "Bearer Token is required.";
        break;
      case "Basic Auth":
        if (!formData.basicAuthClientId) errors.basicAuthClientId = "Client Id is required.";
        if (!formData.basicAuthSecret) errors.basicAuthSecret = "Secret is required.";
        break;
      case "Custom Header":
        if (!formData.customHeaderName) errors.customHeaderName = "Header Name is required.";
        if (!formData.customHeaderValue) errors.customHeaderValue = "Header Value is required.";
        break;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setBtnLoading(true);

    const requestPayload: any = {
      name: formData.name,
      type: formData.type,
      questionnairePrefillUrl: formData.questionnairePrefillUrl,
      questionnaireSaveUrl: formData.questionnaireSaveUrl,
      questionnaireCompleteUrl: formData.questionnaireCompleteUrl,
      authTypeId: authTypeMap[formData.authType],
      billingAuthDetails: [],
    };

    switch (formData.authType) {
      case "HMAC":
        requestPayload.billingAuthDetails.push({
          paramName: "HmacSignatureValue",
          paramValue: formData.hmacSignatureValue
        });
        break;
      case "Bearer Token":
        requestPayload.billingAuthDetails.push({
          paramName: "BearerTokenValue",
          paramValue: formData.bearerTokenValue
        });
        break;
      case "Basic Auth":
        requestPayload.billingAuthDetails.push({
          paramName: "BasicAuthClientId",
          paramValue: formData.basicAuthClientId
        });
        requestPayload.billingAuthDetails.push({
          paramName: "BasicAuthSecret",
          paramValue: formData.basicAuthSecret
        });
        break;
      case "Custom Header":
        requestPayload.billingAuthDetails.push({
          paramName: "CustomHeaderName",
          paramValue: formData.customHeaderName
        });
        requestPayload.billingAuthDetails.push({
          paramName: "CustomHeaderValue",
          paramValue: formData.customHeaderValue
        });
        break;
    }

    dispatch(addB2BEntity(requestPayload)).then(() => {
      setBtnLoading(false);
      handleClose();
    });
  };

  const renderAuthTypeFields = () => {
    switch (formData.authType) {
      case "HMAC":
        return (
          <FormControl fullWidth>
            <label>HMAC Signature Value</label>
            <TextField
              name="hmacSignatureValue"
              value={formData.hmacSignatureValue}
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
              error={!!fieldErrors.hmacSignatureValue}
              helperText={fieldErrors.hmacSignatureValue}
            />
          </FormControl>
        );
      case "Bearer Token":
        return (
          <FormControl fullWidth>
            <label>Token Value</label>
            <TextField
              name="bearerTokenValue"
              value={formData.bearerTokenValue}
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
              error={!!fieldErrors.bearerTokenValue}
              helperText={fieldErrors.bearerTokenValue}
            />
          </FormControl>
        );
      case "Basic Auth":
        return (
          <>
            <FormControl fullWidth>
              <label>Client Id</label>
              <TextField
                name="basicAuthClientId"
                value={formData.basicAuthClientId}
                margin="dense"
                fullWidth
                variant="standard"
                onChange={handleChange}
                error={!!fieldErrors.basicAuthClientId}
                helperText={fieldErrors.basicAuthClientId}
              />
            </FormControl>
            <FormControl fullWidth>
              <label>Secret</label>
              <TextField
                name="basicAuthSecret"
                value={formData.basicAuthSecret}
                margin="dense"
                type="password"
                fullWidth
                variant="standard"
                onChange={handleChange}
                error={!!fieldErrors.basicAuthSecret}
                helperText={fieldErrors.basicAuthSecret}
              />
            </FormControl>
          </>
        );
      case "Custom Header":
        return (
          <>
            <FormControl fullWidth>
              <label>Header Name</label>
              <TextField
                name="customHeaderName"
                value={formData.customHeaderName}
                margin="dense"
                fullWidth
                variant="standard"
                onChange={handleChange}
                error={!!fieldErrors.customHeaderName}
                helperText={fieldErrors.customHeaderName}
              />
            </FormControl>
            <FormControl fullWidth>
              <label>Header Value</label>
              <TextField
                name="customHeaderValue"
                value={formData.customHeaderValue}
                margin="dense"
                fullWidth
                variant="standard"
                onChange={handleChange}
                error={!!fieldErrors.customHeaderValue}
                helperText={fieldErrors.customHeaderValue}
              />
            </FormControl>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle>
        <span className="text-lg text-gray-900">Add New Entity</span>
      </DialogTitle>
      <DialogContent>
        <div className="userViewDetail">
          <FormControl fullWidth>
            <label>Name</label>
            <TextField
              name="name"
              value={formData.name}
              autoFocus
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
          </FormControl>

          <FormControl fullWidth>
            <label>Type</label>
            <Select
              name="type"
              value={formData.type}
              onChange={handleChange}
              error={!!fieldErrors.type}
            >
              <MenuItem value="UsaPath">UsaPath</MenuItem>
              <MenuItem value="LawyerApp">LawyerApp</MenuItem>
              <MenuItem value="ThirdParty">ThirdParty</MenuItem>
            </Select>
            {fieldErrors.type && <div style={{ color: "#d32f2f", fontSize: "0.75rem" }}>{fieldErrors.type}</div>}
          </FormControl>

          <FormControl fullWidth>
            <label>Questionnaire Prefill Url</label>
            <TextField
              name="questionnairePrefillUrl"
              value={formData.questionnairePrefillUrl}
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <label>Questionnaire Save Url</label>
            <TextField
              name="questionnaireSaveUrl"
              value={formData.questionnaireSaveUrl}
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <label>Questionnaire Complete Url</label>
            <TextField
              name="questionnaireCompleteUrl"
              value={formData.questionnaireCompleteUrl}
              margin="dense"
              fullWidth
              variant="standard"
              onChange={handleChange}
            />
          </FormControl>

          <FormControl fullWidth>
            <label>Auth Type</label>
            <Select name="authType" value={formData.authType} onChange={handleChange}>
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="HMAC">HMAC</MenuItem>
              <MenuItem value="Bearer Token">Bearer Token</MenuItem>
              <MenuItem value="Basic Auth">Basic Auth</MenuItem>
              <MenuItem value="Custom Header">Custom Header</MenuItem>
            </Select>
          </FormControl>

          {renderAuthTypeFields()}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" onClick={handleUpdate} disabled={btnLoading}>
          {btnLoading ? "Saving..." : "Add"}
        </Button>
        <Button variant="contained" color="inherit" onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}